import { DIAGNOSTIC_ROUTE_PARAM_ALLOWLIST, DIAGNOSTIC_VARIANT_ALLOWLIST } from './allowlists';
import {
  DIAGNOSTIC_LIMITS,
  DIAGNOSTIC_SCHEMA_VERSION,
  DiagnosticCartEntry,
  DiagnosticCartSummaryKind,
  DiagnosticContextContribution,
  DiagnosticContextV1,
  DiagnosticMoneySource,
  DiagnosticMoneyValue,
  DiagnosticPriceContribution,
  DiagnosticPricingDiscrepancy,
} from './types';

type UnknownRecord = Record<string, unknown>;

const MONEY_SOURCES = new Set<DiagnosticMoneySource>([
  'menu_calculation',
  'local_cart',
  'local_estimate',
  'displayed_ui',
  'pricing_request',
  'pricing_backend',
  'diagnostic_comparison',
]);
const MONEY_AVAILABILITY = new Set(['available', 'loading', 'unavailable']);
const CART_SUMMARY_KINDS = new Set<DiagnosticCartSummaryKind>([
  'item_total_sum',
  'subtotal',
  'discount',
  'delivery_fee',
  'service_fee',
  'small_cart_fee',
  'geographical_fee',
  'fee',
  'surcharge',
  'tax',
  'total',
]);

const record = (value: unknown): UnknownRecord | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;

const text = (value: unknown, max: number = DIAGNOSTIC_LIMITS.string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized ? normalized.slice(0, max) : undefined;
};

const finiteNumber = (value: unknown, { integer = false } = {}): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) > 1_000_000_000_000) {
    return undefined;
  }
  return integer && !Number.isInteger(value) ? undefined : value;
};

const boolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const safeErrorText = (value: unknown, max: number): string | undefined => {
  const sanitized = text(value, max);
  if (
    sanitized &&
    /(?:bearer\s+|authorization|cookie|password|refresh.?token|access.?token|[\w.+-]+@[\w.-]+\.[a-z]{2,})/i.test(
      sanitized
    )
  ) {
    return undefined;
  }
  return sanitized;
};

const stringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const result = value.slice(0, DIAGNOSTIC_LIMITS.array).flatMap(item => {
    const sanitized = text(item);
    return sanitized ? [sanitized] : [];
  });
  return result.length ? result : undefined;
};

const compact = <T extends UnknownRecord>(value: T): T | undefined => {
  const entries = Object.entries(value).filter(([, item]) => item !== undefined);
  return entries.length ? (Object.fromEntries(entries) as T) : undefined;
};

export const sanitizeMoney = (value: unknown): DiagnosticMoneyValue | undefined => {
  const source = record(value);
  const moneySource = text(source?.source, 40) as DiagnosticMoneySource | undefined;
  if (!source || !moneySource || !MONEY_SOURCES.has(moneySource)) {
    return undefined;
  }
  const currency = text(source.currency, 3)?.toUpperCase();
  const availability = text(source.availability, 20);
  const result = compact({
    minorAmount: finiteNumber(source.minorAmount, { integer: true }),
    currency: currency && /^[A-Z]{3}$/.test(currency) ? currency : undefined,
    formatted: text(source.formatted, DIAGNOSTIC_LIMITS.label),
    source: moneySource,
    availability:
      availability && MONEY_AVAILABILITY.has(availability)
        ? (availability as DiagnosticMoneyValue['availability'])
        : undefined,
  });
  if (!result || (result.minorAmount === undefined && !result.formatted && !result.availability)) {
    return undefined;
  }
  return result;
};

const sanitizeContribution = (value: unknown): DiagnosticPriceContribution | undefined => {
  const source = record(value);
  if (!source) {
    return undefined;
  }
  const kind = text(source.kind, 20);
  return compact({
    kind:
      kind && ['modifier', 'picker', 'slot', 'child'].includes(kind)
        ? (kind as DiagnosticPriceContribution['kind'])
        : undefined,
    name: text(source.name),
    id: text(source.id),
    sanityId: text(source.sanityId),
    plu: text(source.plu),
    quantity: finiteNumber(source.quantity),
    multiplier: finiteNumber(source.multiplier),
    price: sanitizeMoney(source.price),
  });
};

const contributionArray = (value: unknown): DiagnosticPriceContribution[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const result = value.slice(0, DIAGNOSTIC_LIMITS.array).flatMap(item => {
    const sanitized = sanitizeContribution(item);
    return sanitized ? [sanitized] : [];
  });
  return result.length ? result : undefined;
};

const sanitizeCartEntry = (value: unknown, depth = 0): DiagnosticCartEntry | undefined => {
  const source = record(value);
  if (!source || depth >= DIAGNOSTIC_LIMITS.selectionDepth) {
    return undefined;
  }
  const children = Array.isArray(source.children)
    ? source.children.slice(0, DIAGNOSTIC_LIMITS.array).flatMap(child => {
        const sanitized = sanitizeCartEntry(child, depth + 1);
        return sanitized ? [sanitized] : [];
      })
    : undefined;
  return compact({
    type: text(source.type, 40),
    name: text(source.name),
    id: text(source.id),
    sanityId: text(source.sanityId),
    cartId: text(source.cartId),
    plu: text(source.plu),
    quantity: finiteNumber(source.quantity),
    unitPrice: sanitizeMoney(source.unitPrice),
    lineTotal: sanitizeMoney(source.lineTotal),
    offerId: text(source.offerId),
    rewardId: text(source.rewardId),
    rewardBenefitId: text(source.rewardBenefitId),
    contributions: contributionArray(source.contributions),
    children: children?.length ? children : undefined,
  });
};

const sanitizeContextInternal = (input: unknown): DiagnosticContextV1 => {
  const source = record(input) ?? {};
  const capture = record(source.capture);
  const user = record(source.user);
  const store = record(source.store);
  const pdp = record(source.pdp);
  const cart = record(source.cart);
  const pricing = record(source.pricing);
  const pricingDiscrepancy = record(source.pricingDiscrepancy);

  const routeParams = record(capture?.routeParams);
  const variants = record(capture?.variants);
  const sanitizedRouteParams = routeParams
    ? Object.fromEntries(
        Object.entries(routeParams)
          .filter(([key]) => DIAGNOSTIC_ROUTE_PARAM_ALLOWLIST.has(key))
          .slice(0, DIAGNOSTIC_LIMITS.array)
          .flatMap(([key, value]) => {
            const safeKey = text(key, DIAGNOSTIC_LIMITS.label);
            const safeValue = text(value);
            return safeKey && safeValue ? [[safeKey, safeValue]] : [];
          })
      )
    : undefined;
  const sanitizedVariants = variants
    ? Object.fromEntries(
        Object.entries(variants)
          .filter(([key]) => (DIAGNOSTIC_VARIANT_ALLOWLIST as readonly string[]).includes(key))
          .slice(0, DIAGNOSTIC_LIMITS.array)
          .flatMap(([key, value]) => {
            const safeKey = text(key, DIAGNOSTIC_LIMITS.label);
            const safeValue =
              typeof value === 'boolean' ? value : text(value, DIAGNOSTIC_LIMITS.label);
            return safeKey && safeValue !== undefined ? [[safeKey, safeValue]] : [];
          })
      )
    : undefined;

  const authState = text(user?.authenticationState, 40);
  const selectionState = text(store?.selectionState, 20);
  const hasLoadedAuthenticatedProfile = authState === 'authenticated';
  const hasSelectedStore = selectionState === 'selected';
  const implementation = text(pdp?.implementation, 20);
  const cartImplementation = text(cart?.effectiveImplementation, 20);
  const experimentCartImplementation = text(cart?.experimentSelectedImplementation, 20);
  const pdpMode = text(pdp?.mode, 10);
  const sourceApi = text(pricing?.sourceApi, 20);
  const phase = text(pricing?.phase, 20);
  const pricingInput = record(pricing?.input);
  const pricingError = record(pricing?.error);
  const discrepancyClassification = text(pricingDiscrepancy?.classification, 40);
  const discrepancySurface = text(pricingDiscrepancy?.surface, 20);
  const discrepancyCorrelation = text(pricingDiscrepancy?.correlation, 40);
  const discrepancyRequestedSubtotal = sanitizeMoney(pricingDiscrepancy?.requestedSubtotal);
  const discrepancyServerSubtotal = sanitizeMoney(pricingDiscrepancy?.serverSubtotal);
  const discrepancyDifference = sanitizeMoney(pricingDiscrepancy?.difference);

  const summaryRows = Array.isArray(cart?.displayedSummaryRows)
    ? cart!.displayedSummaryRows.slice(0, DIAGNOSTIC_LIMITS.array).flatMap(value => {
        const row = record(value);
        const kind = text(row?.kind, 40) as DiagnosticCartSummaryKind | undefined;
        const money = sanitizeMoney(row?.money);
        return kind && CART_SUMMARY_KINDS.has(kind) && money
          ? [{ kind, label: text(row?.label, DIAGNOSTIC_LIMITS.label), money }]
          : [];
      })
    : undefined;

  return {
    schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
    capture: capture
      ? compact({
          capturedAt: text(capture.capturedAt, 40) ?? new Date(0).toISOString(),
          pathname: text(capture.pathname),
          routeParams:
            sanitizedRouteParams && Object.keys(sanitizedRouteParams).length
              ? sanitizedRouteParams
              : undefined,
          platform: text(capture.platform, 30),
          osVersion: text(capture.osVersion, 60),
          appVersion: text(capture.appVersion, 60),
          buildVersion: text(capture.buildVersion, 60),
          otaUpdateId: text(capture.otaUpdateId),
          otaChannel: text(capture.otaChannel),
          commitId: text(capture.commitId),
          easBuildId: text(capture.easBuildId),
          environment: text(capture.environment, 40),
          region: text(capture.region, 20),
          locale: text(capture.locale, 40),
          variants:
            sanitizedVariants && Object.keys(sanitizedVariants).length
              ? sanitizedVariants
              : undefined,
        })
      : undefined,
    user:
      user &&
      authState &&
      ['anonymous', 'authenticated_profile_loading', 'authenticated'].includes(authState)
        ? compact({
            authenticationState: authState as DiagnosticContextV1['user'] extends infer U
              ? U extends { authenticationState: infer A }
                ? A
                : never
              : never,
            name: hasLoadedAuthenticatedProfile ? text(user.name) : undefined,
            email: hasLoadedAuthenticatedProfile ? text(user.email) : undefined,
            cognitoId: hasLoadedAuthenticatedProfile ? text(user.cognitoId) : undefined,
            legacyCognitoId: hasLoadedAuthenticatedProfile ? text(user.legacyCognitoId) : undefined,
            loyaltyId: hasLoadedAuthenticatedProfile ? text(user.loyaltyId) : undefined,
            loyaltyProfileLoaded: hasLoadedAuthenticatedProfile
              ? boolean(user.loyaltyProfileLoaded)
              : undefined,
            loyaltyProfileId: hasLoadedAuthenticatedProfile
              ? text(user.loyaltyProfileId)
              : undefined,
            identitiesCorrespond: hasLoadedAuthenticatedProfile
              ? boolean(user.identitiesCorrespond)
              : undefined,
          })
        : undefined,
    store:
      store && selectionState && ['none', 'selected'].includes(selectionState)
        ? compact({
            selectionState: selectionState as 'none' | 'selected',
            number: hasSelectedStore ? text(store.number) : undefined,
            sanityId: hasSelectedStore ? text(store.sanityId) : undefined,
            name: hasSelectedStore ? text(store.name) : undefined,
            publicAddress: hasSelectedStore
              ? text(store.publicAddress, DIAGNOSTIC_LIMITS.longString)
              : undefined,
            posVendor: hasSelectedStore ? text(store.posVendor) : undefined,
            serviceMode: hasSelectedStore ? text(store.serviceMode) : undefined,
            localTime: hasSelectedStore ? text(store.localTime) : undefined,
            activeDayparts: hasSelectedStore ? stringArray(store.activeDayparts) : undefined,
            configuredDayparts: hasSelectedStore
              ? stringArray(store.configuredDayparts)
              : undefined,
            serviceModeHours: hasSelectedStore
              ? text(store.serviceModeHours, DIAGNOSTIC_LIMITS.longString)
              : undefined,
            daypartHours: hasSelectedStore
              ? text(store.daypartHours, DIAGNOSTIC_LIMITS.longString)
              : undefined,
            availability: hasSelectedStore ? text(store.availability) : undefined,
            isOneMenu: hasSelectedStore ? boolean(store.isOneMenu) : undefined,
          })
        : undefined,
    pdp:
      pdp && implementation && ['one_menu', 'legacy'].includes(implementation)
        ? compact({
            implementation: implementation as 'one_menu' | 'legacy',
            menuObjectType: text(pdp.menuObjectType),
            rootId: text(pdp.rootId),
            selectedId: text(pdp.selectedId),
            sanityId: text(pdp.sanityId),
            name: text(pdp.name),
            plu: text(pdp.plu),
            quantity: finiteNumber(pdp.quantity),
            step: text(pdp.step),
            mode:
              pdpMode && ['add', 'edit'].includes(pdpMode)
                ? (pdpMode as 'add' | 'edit')
                : undefined,
            cartEntryId: text(pdp.cartEntryId),
            offerId: text(pdp.offerId),
            rewardId: text(pdp.rewardId),
            rewardBenefitId: text(pdp.rewardBenefitId),
            baseUnitPrice: sanitizeMoney(pdp.baseUnitPrice),
            configuredUnitPrice: sanitizeMoney(pdp.configuredUnitPrice),
            displayedTotal: sanitizeMoney(pdp.displayedTotal),
            selections: contributionArray(pdp.selections),
          })
        : undefined,
    cart:
      cart && cartImplementation && ['legacy', 'minimal'].includes(cartImplementation)
        ? compact({
            effectiveImplementation: cartImplementation as 'legacy' | 'minimal',
            experimentSelectedImplementation:
              experimentCartImplementation &&
              ['legacy', 'minimal'].includes(experimentCartImplementation)
                ? (experimentCartImplementation as 'legacy' | 'minimal')
                : undefined,
            recoveryOverride: boolean(cart.recoveryOverride),
            legacyEntryCount: finiteNumber(cart.legacyEntryCount, { integer: true }),
            minimalEntryCount: finiteNumber(cart.minimalEntryCount, { integer: true }),
            rbiOrderId: text(cart.rbiOrderId),
            entries: Array.isArray(cart.entries)
              ? cart.entries.slice(0, DIAGNOSTIC_LIMITS.array).flatMap(value => {
                  const sanitized = sanitizeCartEntry(value);
                  return sanitized ? [sanitized] : [];
                })
              : undefined,
            displayedSummaryRows: summaryRows?.length ? summaryRows : undefined,
          })
        : undefined,
    pricing:
      pricing &&
      sourceApi &&
      ['priceOrder', 'priceOrderV2'].includes(sourceApi) &&
      phase &&
      ['requesting', 'polling', 'succeeded', 'failed'].includes(phase)
        ? compact({
            logicalAttemptId: finiteNumber(pricing.logicalAttemptId, { integer: true }) ?? 0,
            sourceApi: sourceApi as 'priceOrder' | 'priceOrderV2',
            attemptStartedAt: text(pricing.attemptStartedAt, 40) ?? new Date(0).toISOString(),
            latestRequestAt: text(pricing.latestRequestAt, 40) ?? new Date(0).toISOString(),
            requestCount: finiteNumber(pricing.requestCount, { integer: true }) ?? 1,
            retryCount: finiteNumber(pricing.retryCount, { integer: true }) ?? 0,
            phase: phase as 'requesting' | 'polling' | 'succeeded' | 'failed',
            input: pricingInput
              ? compact({
                  storeNumber: text(pricingInput.storeNumber),
                  serviceMode: text(pricingInput.serviceMode),
                  cartEntryCount: finiteNumber(pricingInput.cartEntryCount, { integer: true }),
                  requestedTotal: sanitizeMoney(pricingInput.requestedTotal),
                  checkoutRepriceRequestId: text(pricingInput.checkoutRepriceRequestId),
                  requestId: text(pricingInput.requestId),
                  correlationId: text(pricingInput.correlationId),
                })
              : undefined,
            rbiOrderId: text(pricing.rbiOrderId),
            pricingStatus: text(pricing.pricingStatus),
            orderStatus: text(pricing.orderStatus),
            deliveryStatus: text(pricing.deliveryStatus),
            returnedTotal: sanitizeMoney(pricing.returnedTotal),
            error: pricingError
              ? compact({
                  code: safeErrorText(pricingError.code, DIAGNOSTIC_LIMITS.label),
                  message: safeErrorText(pricingError.message, DIAGNOSTIC_LIMITS.longString),
                })
              : undefined,
          })
        : undefined,
    pricingDiscrepancy:
      pricingDiscrepancy &&
      discrepancyClassification === 'confirmed_price_mismatch' &&
      discrepancySurface &&
      ['cart', 'checkout'].includes(discrepancySurface) &&
      discrepancyCorrelation &&
      ['current_cart_request', 'current_checkout_selection'].includes(discrepancyCorrelation) &&
      discrepancyRequestedSubtotal &&
      discrepancyServerSubtotal &&
      discrepancyDifference
        ? compact({
            classification: discrepancyClassification as 'confirmed_price_mismatch',
            surface: discrepancySurface as DiagnosticPricingDiscrepancy['surface'],
            correlation: discrepancyCorrelation as DiagnosticPricingDiscrepancy['correlation'],
            observedAt: text(pricingDiscrepancy.observedAt, 40) ?? new Date(0).toISOString(),
            requestedSubtotal: discrepancyRequestedSubtotal,
            serverSubtotal: discrepancyServerSubtotal,
            difference: discrepancyDifference,
            rbiOrderId: text(pricingDiscrepancy.rbiOrderId),
            orderStatus: text(pricingDiscrepancy.orderStatus),
          })
        : undefined,
    warnings: stringArray(source.warnings)?.slice(0, DIAGNOSTIC_LIMITS.warnings),
  };
};

export const sanitizeDiagnosticContext = (input: unknown): DiagnosticContextV1 =>
  sanitizeContextInternal(input);

export const sanitizeDiagnosticContribution = (input: unknown): DiagnosticContextContribution => {
  const {
    schemaVersion: _schemaVersion,
    warnings: _warnings,
    ...contribution
  } = sanitizeContextInternal({
    schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
    ...(record(input) ?? {}),
  });
  return contribution;
};
